/**
 * A puja's preparation lists: the devotee's prep checklist and the provision split — what the
 * pandit brings and what the household keeps ready (docs/05-admin/10 §3; content 02 §5).
 *
 * **Built from the spec, not a design.** The design's puja drawer shows only a "Samagri list"
 * count; the owner ruled on 2026-10-26 that design-less screens are built from the docs with the
 * console's own components and restyled later.
 *
 * Both lists are whole-list replaces (`PUT /v1/admin/pujas/{id}/prep-items`, `/provisions`): the
 * array sent becomes the list and its order becomes `sort_order`. So each editor stages its edits
 * and sends only on Save, and the fields this screen does not show — a provision's samagri link,
 * substitute and note — ride along on the row they came with rather than being dropped.
 */
import { useState } from 'react';
import { Button } from '../components/primitives';
import { Input, Select, Toggle } from '../components/forms';
import type { components } from '../api/generated/schema';

export type PrepItem = components['schemas']['PrepItemIn'];
export type Provision = components['schemas']['ProvisionIn'];
type ProvidedBy = Provision['provided_by'];

/** `ProvisionProvidedBy`, closed on the server; the labels are what an operator reads. */
export const PROVIDED_BY: readonly { value: ProvidedBy; label: string }[] = [
  { value: 'provider', label: 'Pandit brings it' },
  { value: 'host', label: 'Household keeps it ready' },
  { value: 'either', label: 'Either, agreed per booking' },
  { value: 'venue', label: 'The venue provides it' },
];

const labelOf = (value: ProvidedBy) => PROVIDED_BY.find((p) => p.value === value)!.label;
const valueOf = (label: string) => PROVIDED_BY.find((p) => p.label === label)!.value;

/** Empty optional text goes to the server as null, never as an empty string it would store. */
const orNull = (s: string | null | undefined) => (s && s.trim() ? s.trim() : null);

/** A row is sendable once it has its English label; the server refuses an empty one (422). */
export function prepPayload(rows: readonly PrepItem[]): PrepItem[] {
  return rows.map((r) => ({ label_en: r.label_en.trim(), label_hi: orNull(r.label_hi) }));
}

export function provisionPayload(rows: readonly Provision[]): Provision[] {
  return rows.map((r) => ({
    ...r,
    label_en: r.label_en.trim(),
    label_hi: orNull(r.label_hi),
    quantity_note: orNull(r.quantity_note),
  }));
}

/** Staged edits over a read list, re-seeded when a different puja's list arrives. */
function useDraft<T>(rows: readonly T[]) {
  const [draft, setDraft] = useState<T[]>([...rows]);
  const [baseline, setBaseline] = useState<readonly T[]>(rows);
  if (baseline !== rows) {
    setBaseline(rows);
    setDraft([...rows]);
  }
  const dirty = JSON.stringify(draft) !== JSON.stringify(rows);
  const update = (index: number, patch: Partial<T>) =>
    setDraft(draft.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  /* No bounds guard: Up is disabled on the first row and Down on the last. */
  const move = (index: number, by: number) => {
    const next = [...draft];
    [next[index], next[index + by]] = [next[index + by]!, next[index]!];
    setDraft(next);
  };
  const remove = (index: number) => setDraft(draft.filter((_, j) => j !== index));
  return { draft, setDraft, dirty, update, move, remove };
}

const ROW_STYLE = {
  display: 'grid',
  gap: 'var(--sp-xs)',
  padding: 'var(--sp-sm) var(--sp-md)',
  border: '1px solid var(--c-border)',
  borderRadius: 'var(--r-tile)',
  background: 'var(--c-surface2)',
} as const;

const NOTE_STYLE = { color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-colhead)' } as const;

function RowControls({
  index,
  count,
  disabled,
  onMove,
  onRemove,
}: {
  index: number;
  count: number;
  disabled: boolean;
  onMove: (by: number) => void;
  onRemove: () => void;
}) {
  return (
    <>
      <Button size="sm" variant="ghost" disabled={disabled || index === 0} onClick={() => onMove(-1)}>
        Up
      </Button>
      <Button size="sm" variant="ghost" disabled={disabled || index === count - 1} onClick={() => onMove(1)}>
        Down
      </Button>
      <Button size="sm" variant="ghost" disabled={disabled} onClick={onRemove}>
        Remove
      </Button>
    </>
  );
}

function SaveBar({
  what,
  dirty,
  blank,
  full = false,
  disabled,
  onAdd,
  onSave,
}: {
  what: string;
  dirty: boolean;
  blank: boolean;
  full?: boolean;
  disabled: boolean;
  onAdd: () => void;
  onSave: () => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
      <Button size="sm" variant="secondary" disabled={disabled || full} onClick={onAdd}>
        Add {what}
      </Button>
      <Button size="sm" disabled={disabled || !dirty || blank} onClick={onSave}>
        Save {what}s
      </Button>
      {blank && <span style={{ ...NOTE_STYLE, color: 'var(--c-warn-text)' }}>Every {what} needs an English label.</span>}
      {dirty && !blank && (
        <span style={{ ...NOTE_STYLE, color: 'var(--c-warn-text)' }}>Unsaved — a devotee still sees the old list.</span>
      )}
    </div>
  );
}

/** The devotee's prep checklist — at most 30 lines (`PrepItemSetIn`). */
export function PrepChecklistEditor({
  rows,
  disabled = false,
  onSave,
}: {
  rows: readonly PrepItem[];
  disabled?: boolean;
  onSave: (items: PrepItem[]) => void;
}) {
  const { draft, setDraft, dirty, update, move, remove } = useDraft(rows);
  const blank = draft.some((r) => !r.label_en.trim());
  const full = draft.length >= 30;

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginTop: 'var(--sp-md)' }}>
      {draft.length === 0 && <p style={NOTE_STYLE}>Nothing on the checklist yet.</p>}
      {draft.map((row, i) => (
        <div key={i} style={ROW_STYLE}>
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'end' }}>
            <span style={NOTE_STYLE}>{i + 1}</span>
            <Input
              label={`Checklist item ${i + 1}`}
              value={row.label_en}
              onChange={(v) => update(i, { label_en: v })}
              density="admin"
              disabled={disabled}
              style={{ flex: 1 }}
            />
            <Input
              label={`Hindi, item ${i + 1}`}
              value={row.label_hi ?? ''}
              onChange={(v) => update(i, { label_hi: v })}
              density="admin"
              disabled={disabled}
              style={{ flex: 1 }}
            />
            <RowControls
              index={i}
              count={draft.length}
              disabled={disabled}
              onMove={(by) => move(i, by)}
              onRemove={() => remove(i)}
            />
          </div>
        </div>
      ))}
      <SaveBar
        what="checklist item"
        dirty={dirty}
        blank={blank}
        full={full}
        disabled={disabled}
        onAdd={() => setDraft([...draft, { label_en: '', label_hi: null }])}
        onSave={() => onSave(prepPayload(draft))}
      />
      {full && <p style={NOTE_STYLE}>Thirty lines is the most a checklist holds; remove one to add another.</p>}
    </div>
  );
}

/**
 * The provision split. "Critical" marks a line the booking cannot go ahead without — the devotee's
 * pre-visit reminder leads with those, so the flag is shown on every line rather than tucked away.
 */
export function ProvisionEditor({
  rows,
  disabled = false,
  onSave,
}: {
  rows: readonly Provision[];
  disabled?: boolean;
  onSave: (items: Provision[]) => void;
}) {
  const { draft, setDraft, dirty, update, move, remove } = useDraft(rows);
  const blank = draft.some((r) => !r.label_en.trim());
  const brings = draft.filter((r) => r.provided_by === 'provider').length;
  const keeps = draft.filter((r) => r.provided_by === 'host').length;

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginTop: 'var(--sp-md)' }}>
      <p style={NOTE_STYLE}>
        {draft.length} lines · {brings} the pandit brings · {keeps} the household keeps ready ·{' '}
        {draft.filter((r) => r.is_critical).length} critical
      </p>
      {draft.map((row, i) => (
        <div key={i} style={ROW_STYLE}>
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'end' }}>
            <span style={NOTE_STYLE}>{i + 1}</span>
            <Input
              label={`Provision ${i + 1}`}
              value={row.label_en}
              onChange={(v) => update(i, { label_en: v })}
              density="admin"
              disabled={disabled}
              style={{ flex: 1 }}
            />
            <Select
              density="admin"
              label={`Who provides item ${i + 1}`}
              options={PROVIDED_BY.map((p) => p.label)}
              value={labelOf(row.provided_by)}
              disabled={disabled}
              onChange={(label) => update(i, { provided_by: valueOf(label) })}
            />
            <RowControls
              index={i}
              count={draft.length}
              disabled={disabled}
              onMove={(by) => move(i, by)}
              onRemove={() => remove(i)}
            />
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'end' }}>
            <Input
              label={`Hindi, provision ${i + 1}`}
              value={row.label_hi ?? ''}
              onChange={(v) => update(i, { label_hi: v })}
              density="admin"
              disabled={disabled}
              style={{ flex: 1 }}
            />
            <Input
              label={`Quantity, provision ${i + 1}`}
              value={row.quantity_note ?? ''}
              onChange={(v) => update(i, { quantity_note: v })}
              placeholder="e.g. 1 kg, 11 pieces"
              density="admin"
              disabled={disabled}
              style={{ flex: 1 }}
            />
            <span style={NOTE_STYLE}>Critical</span>
            <Toggle
              label={`Critical, provision ${i + 1}`}
              on={row.is_critical}
              locked={disabled}
              lockedReason="Your role cannot edit the catalogue"
              onChange={(next) => update(i, { is_critical: next })}
            />
          </div>
          {(row.substitute_note || row.note) && (
            <p style={NOTE_STYLE}>
              {[row.substitute_note && `Substitute: ${row.substitute_note}`, row.note].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      ))}
      <SaveBar
        what="provision"
        dirty={dirty}
        blank={blank}
        disabled={disabled}
        onAdd={() => setDraft([...draft, { label_en: '', label_hi: null, provided_by: 'provider', is_critical: false }])}
        onSave={() => onSave(provisionPayload(draft))}
      />
    </div>
  );
}
